/**
 * ReservationChatPanel — 自社チャット（予約チャット・匿名チャット）の会話パネル
 *
 * LINE ページのチャット・モーダル内で、自社チャットのインスタンス（kind='resv' / 'anon'）を開いたときに
 * LineChatPanel の代わりに描画する。見た目は LineChat.css のクラスを流用（受信=inbound / 送信=outbound）。
 *
 * 2026-10-26（LP 管理画面 /admin の統合・booking-platform/md/customer-chat.md §13）:
 *   匿名チャット（kind='anon'）も開ける。/admin にしか無かった **対応中・メモ・IP/国ブロック・テンプレート** を足し、
 *   予約チャットでは **予約前の会話**（匿名の会話のうち本人確認でこの予約に結びついたもの）と
 *   **LINE の紐づけ候補**（同じお客様らしい LINE ユーザー・1 クリックで予約に紐づけ）を出す。
 *
 * できること: メッセージ一覧・スタッフ名義の返信・既読（readMode に従う）・予約情報の表示と「詳細」・
 *   AI 返信案（LP 側 suggest_reply）・受信メッセージの翻訳表示（LP 側が保存した translated_text）・
 *   **送信時翻訳**（🌐 自動翻訳 ON: 日本語→お客様の言語。LP の translate → send_message に原文を添える。2026-10-11）・
 *   **画像**（お客様の画像の表示・スタッフからの送信。LP の upload_image / 署名付き URL。2026-10-11）。
 * やらないこと: 引用（LINE 固有）。テンプレートは LINE のテンプレートを使う（LINE が無い店舗＝宮古では出さない）。
 * リアルタイム: LP の Pusher（services/reservationChatPusher）で新着を受け、ポーリングは保険（接続中 60 秒・未接続 5 秒）。
 * 設計: md/master_md/page-line-chat.md「自社チャットの統合」
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Menu, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EditNoteIcon from '@mui/icons-material/EditNote';
import BlockIcon from '@mui/icons-material/Block';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LinkIcon from '@mui/icons-material/Link';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { reservationSyncService } from '../services/reservationSyncService';
import { useReservationChat, type ReservationChatSuggestion } from '../hooks/useReservationChat';
import {
  countUnreadMessages, detectCustomerLang, validateResvImage, RESV_TRANSLATE_LANGS,
  type ReservationChatMessage,
} from '../lib/reservationChat';
import { panelPollInterval } from '../lib/reservationChatRealtime';
import { logEvent } from '../lib/eventLog';
import { preReservationSessions } from '../lib/lpChatBoard';
import { renderTemplate, templateDateTime, templateTime } from '../lib/templateRender';
import { checkinUrl, formUrl } from '../lib/customerLinks';
import ReservationChatAdminActions from './ReservationChatAdminActions';
import './LineChat.css';

interface Props {
  /** LineChatBoard の chatInstances の 1 件（kind='resv' / 'anon'） */
  instance: any;
  readMode?: boolean;
  onClose?: () => void;
  /** 一覧の再取得（送信・既読・アーカイブ後） */
  onReload?: () => void;
  /** 予約の「詳細」（LINE カードの詳細と同じ編集パネル） */
  onOpenReservation?: (res: any) => void;
  /** 別の自社チャットの会話を開く（予約前の会話へ移る） */
  onOpenSession?: (sessionKey: string) => void;
  /** 予約チャット: 同じお客様らしい LINE ユーザー（lib/lpChatBoard findLineLinkCandidates） */
  lineLinkCandidates?: any[];
  /** 予約チャット: LINE ユーザーをこの予約に紐づける（sys/line link_reservation） */
  onLinkLine?: (lineUserId: string) => Promise<boolean>;
  /** LINE のテンプレート（useLineChat）。LINE が無い店舗では渡さない＝テンプレートボタンを出さない */
  fetchTemplates?: () => Promise<any[]>;
  fetchTemplateCategories?: () => Promise<any[]>;
}

/** 対応中の長さ（LP 管理画面 AdminHandlingToggle と同じ） */
const HANDLING_PRESETS: ReadonlyArray<{ label: string; minutes: number }> = [
  { label: '15分', minutes: 15 },
  { label: '30分', minutes: 30 },
  { label: '1時間', minutes: 60 },
  { label: '対応終了まで (8時間)', minutes: 480 },
];
const TRIAGE_LABEL: Record<string, { label: string; color: string; bg: string }> = {
  attention: { label: '要対応', color: '#b91c1c', bg: '#fee2e2' },
  auto: { label: '自動応答のみ', color: '#475569', bg: '#f1f5f9' },
  junk: { label: 'ジャンク', color: '#92400e', bg: '#fef3c7' },
};

const LS_AUTO_TRANSLATE = 'resvChat_sendAutoTranslate';
const lsSendLangKey = (sessionKey: string) => `resvChat_sendLang_${sessionKey}`;
const lsGet = (k: string): string | null => {
  try { return window.localStorage.getItem(k); } catch { return null; }
};
const lsSet = (k: string, v: string) => {
  try { window.localStorage.setItem(k, v); } catch { /* ignore */ }
};

const fmtDateTime = (v: unknown): string => {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v.includes('T') ? v : v.replace(/-/g, '/'));
  if (Number.isNaN(d.getTime())) return v;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const senderLabel = (s: string): string => (s === 'admin' ? 'スタッフ' : s === 'system' ? 'システム' : 'お客様');

const ReservationChatPanel: React.FC<Props> = ({
  instance, readMode, onReload, onOpenReservation, onOpenSession, lineLinkCandidates, onLinkLine, fetchTemplates, fetchTemplateCategories,
}) => {
  const sessionKey: string = instance?.session_key;
  const isAnon = instance?.kind === 'anon';
  const {
    sessions, fetchMessages, sendMessage, markRead, setArchived, suggestReply, uploadImage, subscribeMessages, realtimeConnected,
    setHandling, updateMemo, setIpBlocked, setCountryBlocked, setStaffArchived,
  } = useReservationChat();
  const session = sessions.find((s) => s.session_key === sessionKey) || null;
  // /admin から移した操作（対応中・メモ・ブロック・テンプレート・LINE の紐づけ）
  const [handlingAnchor, setHandlingAnchor] = useState<HTMLElement | null>(null);
  const [memoEditing, setMemoEditing] = useState(false);
  const [memoDraft, setMemoDraft] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [templateAnchor, setTemplateAnchor] = useState<HTMLElement | null>(null);
  const [templateList, setTemplateList] = useState<any[]>([]);
  const [templateCats, setTemplateCats] = useState<any[]>([]);

  const [messages, setMessages] = useState<ReservationChatMessage[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<ReservationChatSuggestion | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  // 送信時翻訳: ON/OFF は端末で共通、翻訳先は会話ごと（お客様の言語から自動推定・手で変えたら保存）
  const [sendAutoTranslate, setSendAutoTranslate] = useState<boolean>(() => lsGet(LS_AUTO_TRANSLATE) === '1');
  const [sendLang, setSendLang] = useState<string>('');
  const [sendLangManual, setSendLangManual] = useState(false);
  // 画像
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastIdRef = useRef(0);
  const markedRef = useRef(0);

  const reservationId = instance?.instance_reservation_id || null;
  const resData = instance?.instance_res_data || (reservationId ? reservationSyncService.getReservation(reservationId) : null);
  // isArchived = 対応完了（status=ARCHIVED・お客様のチャットも終了）。スタッフのアーカイブ（一覧から外すだけ）は staffArchived
  const isArchived = (session?.status || (instance?.chat_closed ? 'ARCHIVED' : 'ACTIVE')) === 'ARCHIVED';
  const staffArchived = Boolean(session ? session.staff_archived_at : instance?.staff_archived);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  /** 既読: readMode のときだけ。最新 ID が進んだときだけ呼ぶ（連打しない） */
  const maybeMarkRead = useCallback(
    (latestId: number) => {
      if (!readMode || !latestId || latestId <= markedRef.current) return;
      markedRef.current = latestId;
      markRead(sessionKey, latestId).then((ok) => {
        if (ok && onReload) onReload();
      });
    },
    [readMode, markRead, sessionKey, onReload],
  );

  // 初回読み込み（会話を切り替えたらリセット）
  useEffect(() => {
    let cancelled = false;
    setMessages([]);
    setInitialLoading(true);
    setLoadError(null);
    setSuggestion(null);
    setSuggestError(null);
    setSendError(null);
    setNotice(null);
    setMemoEditing(false);
    lastIdRef.current = 0;
    markedRef.current = 0;
    setSendLangManual(false);
    setSendLang(lsGet(lsSendLangKey(sessionKey)) || '');
    fetchMessages(sessionKey, 0).then((r) => {
      if (cancelled) return;
      setInitialLoading(false);
      if (!r) {
        setLoadError('メッセージを取得できませんでした（ログイン切れ・通信エラーの可能性）');
        return;
      }
      setMessages(r.messages);
      lastIdRef.current = r.messages.length ? r.messages[r.messages.length - 1].id : 0;
      setTimeout(scrollToBottom, 0);
      maybeMarkRead(lastIdRef.current);
    });
    return () => {
      cancelled = true;
    };
    // readMode の切替で再読込はしない（maybeMarkRead は最新 ID で再評価される）。依存は会話キーのみ
  }, [sessionKey]);

  // お客様の言語を推定して翻訳先の初期値にする（スタッフが手で選んだら上書きしない）
  useEffect(() => {
    if (sendLangManual || lsGet(lsSendLangKey(sessionKey))) return;
    const detected = detectCustomerLang(messages);
    if (detected && detected !== sendLang) setSendLang(detected);
  }, [messages, sessionKey, sendLangManual, sendLang]);

  /** 新着を 1 件取り込む（Pusher・ポーリング・自分の送信で共通。重複は id で排除） */
  const appendMessage = useCallback(
    (m: ReservationChatMessage) => {
      setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
      lastIdRef.current = Math.max(lastIdRef.current, m.id);
      setTimeout(scrollToBottom, 0);
    },
    [scrollToBottom],
  );

  // リアルタイム（LP の Pusher private-admin-chat → useReservationChat → この会話だけ）
  useEffect(() => {
    if (initialLoading || loadError) return;
    return subscribeMessages(sessionKey, (m) => {
      appendMessage(m);
      if (m.sender === 'user') maybeMarkRead(Math.max(lastIdRef.current, m.id));
    });
  }, [sessionKey, initialLoading, loadError, subscribeMessages, appendMessage, maybeMarkRead]);

  // 差分ポーリング（開いている間だけ。Pusher 接続中は保険として 60 秒、未接続は 5 秒）
  useEffect(() => {
    if (initialLoading || loadError) return;
    const timer = setInterval(async () => {
      const r = await fetchMessages(sessionKey, lastIdRef.current);
      if (!r || r.messages.length === 0) return;
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.id));
        const fresh = r.messages.filter((m) => !known.has(m.id));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
      lastIdRef.current = Math.max(lastIdRef.current, r.messages[r.messages.length - 1].id);
      setTimeout(scrollToBottom, 0);
      if (r.messages.some((m) => m.sender === 'user')) {
        maybeMarkRead(lastIdRef.current);
        if (onReload) onReload(); // 一覧の最終メッセージ・未読表示を追従させる
      }
    }, panelPollInterval(realtimeConnected));
    return () => clearInterval(timer);
  }, [sessionKey, initialLoading, loadError, fetchMessages, maybeMarkRead, scrollToBottom, onReload, realtimeConnected]);

  // 既読モードを後から ON にしたら、その時点の最新まで既読にする
  useEffect(() => {
    if (readMode && lastIdRef.current) maybeMarkRead(lastIdRef.current);
  }, [readMode, maybeMarkRead]);

  const translateTo = sendAutoTranslate && sendLang && sendLang !== 'ja' ? sendLang : null;
  const sendLangLabel = RESV_TRANSLATE_LANGS.find((l) => l.value === sendLang)?.label || sendLang;

  const nowStamp = () => {
    const now = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())} ${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
  };

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || sending) return;
    setSending(true);
    setSendError(null);
    const r = await sendMessage(sessionKey, text, { translateTo });
    setSending(false);
    if (!r.success) {
      setSendError(`送信に失敗しました: ${r.error || '不明なエラー'}`);
      return; // 本文は入力欄に残す（無言で捨てない。翻訳失敗もここ＝日本語のまま送らない）
    }
    setInputText('');
    setSuggestion(null);
    // 送った分を即時に表示し、次のポーリング／Pusher で確定分と重複しないよう lastId も進める
    appendMessage({
      id: r.id as number,
      sender: 'admin',
      text: r.sent_text || text,
      timestamp: nowStamp(),
      original_text: r.original_text ?? null,
      original_lang: r.original_text ? 'ja' : null,
      translated_text: r.original_text ? r.sent_text || text : null,
      translated_lang: r.translated_lang ?? null,
    });
    if (onReload) onReload();
  };

  const toggleAutoTranslate = () => {
    const next = !sendAutoTranslate;
    setSendAutoTranslate(next);
    lsSet(LS_AUTO_TRANSLATE, next ? '1' : '0');
    if (next && !sendLang) {
      const detected = detectCustomerLang(messages);
      if (detected) setSendLang(detected);
    }
  };

  const handlePickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || uploading) return;
    const err = validateResvImage(file);
    if (err) {
      setSendError(err);
      logEvent({ action: 'resv_chat.upload_image', target: sessionKey, result: 'fail', err: `client: ${err}` });
      return;
    }
    setUploading(true);
    setSendError(null);
    const r = await uploadImage(sessionKey, file);
    setUploading(false);
    if (r.success === false) {
      setSendError(`画像を送信できませんでした: ${r.error}`);
      return;
    }
    appendMessage(r.message);
    if (onReload) onReload();
  };

  const handleSuggest = async () => {
    setSuggesting(true);
    setSuggestError(null);
    const s = await suggestReply(sessionKey);
    setSuggesting(false);
    if (!s) {
      setSuggestError('AI 返信案を取得できませんでした（LP 側で AI が無効か、顧客メッセージが無い可能性）');
      return;
    }
    setSuggestion(s);
  };

  const handleArchive = async () => {
    if (archiving) return;
    const label = isArchived
      ? '対応完了を取り消しますか？\nお客様のチャットも再開します。'
      : '対応完了にしますか？\nお客様の画面には「担当者がチャットを終了しました。」と表示され、お客様は新しいチャットを始め直すことになります。\n（一覧から外すだけなら「アーカイブ」ボタンを使ってください）';
    if (!window.confirm(label)) return;
    setArchiving(true);
    const ok = await setArchived(sessionKey, !isArchived);
    setArchiving(false);
    if (!ok) setSendError(isArchived ? '対応完了を取り消せませんでした' : '対応完了にできませんでした');
    if (onReload) onReload();
  };

  /** アーカイブ（一覧から外すだけ・お客様には影響しない。お客様の新着で自動的に戻る。§13-5） */
  const handleStaffArchive = async () => {
    setBusy('staff_archive');
    const ok = await setStaffArchived([sessionKey], !staffArchived);
    setBusy(null);
    if (!ok) setSendError(staffArchived ? '一覧に戻せませんでした' : 'アーカイブできませんでした');
    else setNotice(staffArchived ? '一覧に戻しました' : 'アーカイブしました（お客様には影響しません。新しいメッセージが来たら一覧に戻ります）');
    if (onReload) onReload();
  };

  const handlingUntil: string | null = session?.admin_handling_until || null;
  const handleHandling = async (minutes: number) => {
    setHandlingAnchor(null);
    setBusy('handling');
    const ok = await setHandling(sessionKey, minutes);
    setBusy(null);
    if (!ok) setSendError(minutes ? '対応中にできませんでした' : '対応中を解除できませんでした');
  };

  const memo: string = (session?.memo ?? instance?.memo ?? '') || '';
  const handleSaveMemo = async () => {
    setBusy('memo');
    const ok = await updateMemo(sessionKey, memoDraft.trim());
    setBusy(null);
    if (!ok) {
      setSendError('メモを保存できませんでした');
      return;
    }
    setMemoEditing(false);
    if (onReload) onReload();
  };

  const clientIp: string | null = session?.client_ip || instance?.client_ip || null;
  const countryCode: string | null = session?.country_code || instance?.country_code || null;
  const countryName: string | null = session?.country || instance?.country || null;
  const isBlocked = Boolean(Number(session?.is_blocked ?? (instance?.is_blocked ? 1 : 0)));
  const blockType: string | null = session?.block_reason_type || instance?.block_reason_type || null;
  const handleBlock = async (kind: 'ip' | 'country', block: boolean) => {
    const target = kind === 'ip' ? clientIp : countryCode;
    if (!target) return;
    const what = kind === 'ip' ? `IP ${target}` : `国 ${countryName || ''}（${target}）`;
    const msg = block
      ? `${what} をブロックしますか？\nこの${kind === 'ip' ? ' IP' : '国'}からは LP のチャットを使えなくなります（予約チャットも含む）。`
      : `${what} のブロックを解除しますか？`;
    if (!window.confirm(msg)) return;
    setBusy(`block_${kind}`);
    const ok = kind === 'ip' ? await setIpBlocked(target, block) : await setCountryBlocked(target, block, countryName);
    setBusy(null);
    if (!ok) setSendError(block ? 'ブロックできませんでした' : 'ブロックを解除できませんでした');
    else setNotice(block ? `${what} をブロックしました` : `${what} のブロックを解除しました`);
  };

  const preResv = isAnon ? [] : preReservationSessions(sessions, instance?.instance_reservation_id);

  const handleLinkLine = async (u: any) => {
    if (!onLinkLine) return;
    const name = u.custom_display_name || u.line_display_name || 'この LINE';
    if (!window.confirm(`LINE「${name}」をこの予約に紐づけますか？\n同じお客様か、会話の内容で確かめてから紐づけてください。`)) return;
    setBusy(`link_${u.line_user_id}`);
    const ok = await onLinkLine(u.line_user_id);
    setBusy(null);
    if (!ok) setSendError('LINE を予約に紐づけられませんでした');
    else setNotice(`LINE「${name}」をこの予約に紐づけました。「すべて」タブでは 1 枚のカードにまとまります`);
  };

  const handleOpenTemplates = async (e: React.MouseEvent<HTMLElement>) => {
    if (!fetchTemplates) return;
    setTemplateAnchor(e.currentTarget);
    const [list, cats] = await Promise.all([fetchTemplates(), fetchTemplateCategories ? fetchTemplateCategories() : Promise.resolve([])]);
    setTemplateList(Array.isArray(list) ? list : []);
    setTemplateCats(Array.isArray(cats) ? cats : []);
  };
  const handleSelectTemplate = (t: any) => {
    setTemplateAnchor(null);
    const res = resData;
    const vars = {
      customer_name: res?.customer_name || session?.resv_customer_name || '',
      start_date: res ? templateDateTime(res.start_date) : '',
      end_date: res ? templateDateTime(res.end_date) : '',
      start_date_time: res ? templateTime(res.start_date) : '',
      end_date_time: res ? templateTime(res.end_date) : '',
      start_time: res ? templateTime(res.start_date) : '',
      end_time: res ? templateTime(res.end_date) : '',
      id: res ? res.id : '',
      contact_code: res?.contact_code || instance?.contact_code || '',
      form: formUrl('https://n-car.jp', res?.contact_code),
      checkin: checkinUrl('https://n-car.jp', res?.contact_code),
      status: res ? (res.status_name || res.status || '') : '',
    };
    setInputText(renderTemplate(String(t.content || ''), vars));
  };

  const unreadNow = countUnreadMessages(messages, session?.admin_last_read_id);
  const displayName: string = instance?.custom_display_name || instance?.line_display_name || (isAnon ? '匿名チャット' : '予約者チャット');
  const triage = TRIAGE_LABEL[instance?.triage || 'auto'];
  const contactCode: string | null = instance?.contact_code || resData?.contact_code || null;

  return (
    <div className="line-chat-window no-header">
      {/* ヘッダー: 予約情報 */}
      <div style={{ background: '#f8fafc', padding: '12px', borderBottom: '1px solid #e2e8f0', fontSize: '13px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {isAnon ? (
            <Chip label="🌐 匿名チャット" size="small" sx={{ fontWeight: 800, bgcolor: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe' }} />
          ) : (
            <Chip label="💬 予約チャット" size="small" sx={{ fontWeight: 800, bgcolor: '#ecfeff', color: '#0e7490', border: '1px solid #a5f3fc' }} />
          )}
          <span style={{ fontWeight: 800, color: '#1e293b', fontSize: '15px' }}>{displayName}</span>
          {contactCode && (
            <span style={{ color: '#475569', fontWeight: 700 }}>{contactCode}</span>
          )}
          {resData && (
            <span
              style={{
                background: resData.bg_color || '#e2e8f0',
                color: resData.text_color || '#475569',
                fontSize: '10px',
                padding: '1px 8px',
                borderRadius: '4px',
                fontWeight: 800,
                border: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              {resData.status_name || resData.status}
            </span>
          )}
          {isArchived && <Chip label="対応完了（終了済み）" size="small" color="warning" variant="outlined" />}
          {staffArchived && <Chip label="アーカイブ中" size="small" variant="outlined" />}
          {isAnon && triage && (
            <span style={{ fontSize: '10px', fontWeight: 800, padding: '1px 8px', borderRadius: '9999px', color: triage.color, background: triage.bg }}>{triage.label}</span>
          )}
          {session?.admin_handling && (
            <Chip label={`対応中${handlingUntil ? `（${fmtDateTime(handlingUntil)} まで）` : ''}`} size="small" color="info" variant="outlined" />
          )}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
            <Tooltip title="対応中（AI の自動返信を止めてスタッフが対応）">
              <span>
                <IconButton size="small" onClick={(e) => setHandlingAnchor(e.currentTarget)} disabled={busy === 'handling' || isArchived}
                  sx={{ color: session?.admin_handling ? '#0284c7' : undefined }} aria-label="対応中">
                  {busy === 'handling' ? <CircularProgress size={16} /> : <HeadsetMicIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Menu anchorEl={handlingAnchor} open={Boolean(handlingAnchor)} onClose={() => setHandlingAnchor(null)} sx={{ zIndex: 10600 }}>
              {HANDLING_PRESETS.map((p) => (
                <MenuItem key={p.minutes} onClick={() => handleHandling(p.minutes)} sx={{ fontSize: '13px' }}>対応中: {p.label}</MenuItem>
              ))}
              {session?.admin_handling && <MenuItem onClick={() => handleHandling(0)} sx={{ fontSize: '13px', color: '#b91c1c' }}>対応中を解除</MenuItem>}
            </Menu>
            <Tooltip title="メモ（スタッフだけが見る）">
              <IconButton size="small" onClick={() => { setMemoDraft(memo); setMemoEditing(true); }} aria-label="メモを編集">
                <EditNoteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {resData && onOpenReservation && (
              <Tooltip title="予約の詳細を開く">
                <IconButton size="small" onClick={() => onOpenReservation(resData)}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={staffArchived ? '一覧に戻す' : 'アーカイブ（一覧から外す。お客様には影響しません）'}>
              <span>
                <IconButton size="small" onClick={handleStaffArchive} disabled={busy === 'staff_archive'} aria-label={staffArchived ? '一覧に戻す' : 'アーカイブ'}>
                  {staffArchived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isArchived ? '対応完了を取り消す（お客様のチャットも再開）' : '対応完了（お客様のチャットも終了）'}>
              <IconButton size="small" onClick={handleArchive} disabled={archiving} aria-label={isArchived ? '対応完了を取り消す' : '対応完了'}>
                {isArchived ? <RestartAltIcon fontSize="small" /> : <TaskAltIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </div>
        <div style={{ marginTop: '6px', color: '#475569', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {isAnon ? (
            <>
              <span>{[instance?.city, instance?.region, countryName].filter(Boolean).join(' · ') || '地域不明'}</span>
              {clientIp && <span style={{ color: '#94a3b8', fontFamily: 'monospace' }}>{clientIp}</span>}
              {isBlocked && (
                <span style={{ color: '#b91c1c', fontWeight: 700 }}>ブロック中（{blockType === 'country' ? '国' : 'IP'}）</span>
              )}
              {instance?.pre_reservation_id && (
                <span style={{ color: '#0e7490', fontWeight: 700 }}>予約前の会話（予約 {instance?.contact_code || `#${instance.pre_reservation_id}`}）</span>
              )}
            </>
          ) : resData ? (
            <>
              <span>
                <span className="line-period-icon pickup">↑</span> {fmtDateTime(resData.start_date)}
              </span>
              <span>
                <span className="line-period-icon return">↓</span> {fmtDateTime(resData.end_date)}
              </span>
              {resData.car_type && <span>{resData.car_type}</span>}
              {reservationId && <span style={{ color: '#94a3b8' }}>#{reservationId}</span>}
            </>
          ) : (
            <span style={{ color: '#94a3b8' }}>
              {reservationId ? `予約 #${reservationId}（表示範囲外のため予約データ未取得）` : '予約データ未取得'}
            </span>
          )}
          {unreadNow > 0 && !readMode && (
            <span style={{ color: '#dc2626', fontWeight: 700 }}>未読 {unreadNow} 件（既読モード OFF）</span>
          )}
        </div>
        {/* メモ（/admin から移した。予約チャットは自動メモ「予約 {連絡コード} {氏名}」が入っている） */}
        {memoEditing ? (
          <div style={{ marginTop: '8px', display: 'flex', gap: '6px', alignItems: 'center' }}>
            <TextField size="small" value={memoDraft} onChange={(e) => setMemoDraft(e.target.value)} placeholder="メモ" fullWidth autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && !(e.nativeEvent as any).isComposing) { e.preventDefault(); handleSaveMemo(); } }}
              inputProps={{ maxLength: 255, 'aria-label': 'メモ' }} />
            <Button size="small" variant="contained" onClick={handleSaveMemo} disabled={busy === 'memo'} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>保存</Button>
            <Button size="small" onClick={() => setMemoEditing(false)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>やめる</Button>
          </div>
        ) : memo ? (
          <div style={{ marginTop: '6px', color: '#be185d', fontSize: '12px' }}>メモ: {memo}</div>
        ) : null}
        {/* 匿名チャット: IP ブロック／国ブロック（/admin から移した） */}
        {isAnon && (clientIp || countryCode) && (
          <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {clientIp && (
              <Button size="small" variant="outlined" color={blockType === 'ip' ? 'inherit' : 'error'} startIcon={<BlockIcon fontSize="small" />}
                disabled={busy === 'block_ip'} onClick={() => handleBlock('ip', blockType !== 'ip')} sx={{ textTransform: 'none', fontSize: '11px', py: 0 }}>
                {blockType === 'ip' ? 'IP ブロックを解除' : 'IP ブロック'}
              </Button>
            )}
            {countryCode && (
              <Button size="small" variant="outlined" color={blockType === 'country' ? 'inherit' : 'error'} startIcon={<BlockIcon fontSize="small" />}
                disabled={busy === 'block_country'} onClick={() => handleBlock('country', blockType !== 'country')} sx={{ textTransform: 'none', fontSize: '11px', py: 0 }}>
                {blockType === 'country' ? `国ブロックを解除（${countryCode}）` : `国ブロック（${countryCode}）`}
              </Button>
            )}
          </div>
        )}
        {!isAnon && (
          <>
            {/* スタッフ操作: 招待リンク（1回限り・期限付き）／下4桁ロックの解除 */}
            <div style={{ marginTop: '8px' }}>
              <ReservationChatAdminActions contactCode={contactCode} reservationId={reservationId} />
            </div>
            {/* 予約前の会話（匿名の会話のうち、本人確認でこの予約に結びついたもの） */}
            {preResv.length > 0 && onOpenSession && (
              <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px' }}>
                <span style={{ color: '#64748b' }}>予約前の会話:</span>
                {preResv.map((p) => (
                  <Button key={p.session_key} size="small" variant="text" onClick={() => onOpenSession(p.session_key)} sx={{ textTransform: 'none', fontSize: '12px', py: 0 }}>
                    🌐 {fmtDateTime(p.updated_at)}
                  </Button>
                ))}
              </div>
            )}
            {/* 同じお客様らしい LINE（自動では紐づけない） */}
            {lineLinkCandidates && lineLinkCandidates.length > 0 && onLinkLine && (
              <div style={{ marginTop: '8px', padding: '8px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: '12px' }}>
                <div style={{ color: '#166534', fontWeight: 700, marginBottom: '4px' }}>
                  このお客様は LINE にも書いているかもしれません（名前が一致・予約に未紐づけ）
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {lineLinkCandidates.map((u) => (
                    <Button key={u.line_user_id} size="small" variant="outlined" color="success" startIcon={<LinkIcon fontSize="small" />}
                      disabled={busy === `link_${u.line_user_id}`} onClick={() => handleLinkLine(u)} sx={{ textTransform: 'none', fontSize: '11px', py: 0 }}>
                      LINE「{u.custom_display_name || u.line_display_name}」を紐づける{u.last_message_at ? `（最終 ${fmtDateTime(u.last_message_at)}）` : ''}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {notice && <div style={{ marginTop: '6px', color: '#166534', fontSize: '12px' }}>{notice}</div>}
      </div>

      {/* メッセージ一覧 */}
      <div className="line-message-list" ref={scrollRef}>
        {initialLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px', color: '#64748b' }}>
            <CircularProgress size={32} sx={{ color: '#94a3b8' }} />
            <span style={{ fontSize: '13px' }}>メッセージを読み込み中...</span>
          </div>
        )}
        {loadError && (
          <div style={{ textAlign: 'center', color: '#b91c1c', marginTop: '20px', fontSize: '13px' }}>{loadError}</div>
        )}
        {!initialLoading && !loadError && messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', marginTop: '20px', fontSize: '14px' }}>メッセージはありません</div>
        )}
        {messages.map((m) => {
          const dir = m.sender === 'user' ? 'inbound' : 'outbound';
          const isSystem = m.sender === 'system';
          return (
            <div
              key={m.id}
              className={`line-message-bubble ${dir} type-text`}
              style={isSystem ? { background: '#f1f5f9', color: '#64748b', alignSelf: 'center', fontSize: '12px' } : undefined}
            >
              <div className="message-content" style={{ whiteSpace: 'pre-wrap' }}>
                {m.attachment && m.attachment.kind === 'image' ? (
                  m.attachment.url ? (
                    <a href={m.attachment.url} target="_blank" rel="noopener noreferrer" title="画像を開く（別タブ）">
                      <img
                        src={m.attachment.thumb_url || m.attachment.url}
                        alt="画像"
                        loading="lazy"
                        style={{ maxWidth: '240px', maxHeight: '240px', borderRadius: '8px', display: 'block' }}
                      />
                    </a>
                  ) : (
                    <span style={{ color: '#b91c1c' }}>画像（URL を発行できませんでした。LP の MEDIA_SIGNING_KEY を確認）</span>
                  )
                ) : (
                  m.text
                )}
                {m.sender === 'user' && m.translated_text && m.translated_text !== m.text && (
                  <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed rgba(0,0,0,0.15)', fontSize: '12px', color: '#475569' }}>
                    {m.translated_lang ? `(${String(m.translated_lang).toUpperCase()}) ` : ''}
                    {m.translated_text}
                  </div>
                )}
                {m.sender === 'admin' && m.original_text && m.original_text !== m.text && (
                  <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed rgba(255,255,255,0.4)', fontSize: '12px', opacity: 0.85 }}>
                    原文: {m.original_text}
                  </div>
                )}
              </div>
              <div className="message-time">
                {senderLabel(m.sender)} · {fmtDateTime(m.timestamp)}
              </div>
            </div>
          );
        })}
      </div>

      {/* 入力 */}
      <div className="line-input-area" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px', padding: '12px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        {suggestion && (
          <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#eef2ff', border: '1px solid #c7d2fe', fontSize: '13px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <SmartToyIcon fontSize="small" sx={{ color: '#4f46e5' }} />
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#3730a3' }}>
                AI 返信案{suggestion.cached ? '（直近の案）' : ''} · 確度 {Math.round((suggestion.confidence || 0) * 100)}%
                {suggestion.alert_level > 0 ? ` · 注意レベル ${suggestion.alert_level}` : ''}
              </Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                <Button size="small" variant="contained" onClick={() => setInputText(suggestion.reply)} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                  入力欄へ
                </Button>
                <Button size="small" variant="text" onClick={() => setSuggestion(null)} sx={{ textTransform: 'none' }}>
                  閉じる
                </Button>
              </Box>
            </Box>
            <div style={{ whiteSpace: 'pre-wrap', color: '#1e293b' }}>{suggestion.reply}</div>
            {suggestion.reasoning && (
              <div style={{ marginTop: '4px', fontSize: '11px', color: '#64748b' }}>{suggestion.reasoning}</div>
            )}
          </Box>
        )}
        {(sendError || suggestError) && (
          <div style={{ color: '#b91c1c', fontSize: '12px' }}>{sendError || suggestError}</div>
        )}
        {/* 送信時翻訳（LINE ページと同じ見せ方。翻訳は LP の translate＝Gemini。失敗したら送らない） */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Box
            display="flex"
            alignItems="center"
            sx={{
              bgcolor: sendAutoTranslate ? '#dcfce7' : '#f1f5f9',
              border: sendAutoTranslate ? '1px solid #86efac' : '1px solid #e2e8f0',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <Tooltip title={translateTo ? `送信時に日本語→${sendLangLabel}に翻訳して送ります（原文も保存）` : sendAutoTranslate ? '翻訳先の言語を選んでください' : '翻訳なしで送信'}>
              <IconButton
                size="small"
                onClick={toggleAutoTranslate}
                disabled={isArchived}
                sx={{ color: sendAutoTranslate ? '#16a34a' : '#94a3b8', borderRadius: 0, px: 1.5, py: 0.5, fontSize: '12px', fontWeight: 700, gap: '4px', minWidth: 0 }}
              >
                <span style={{ fontSize: '14px' }}>🌐</span>
                <span style={{ fontSize: '11px' }}>自動翻訳{sendAutoTranslate ? 'ON' : ''}</span>
              </IconButton>
            </Tooltip>
            {sendAutoTranslate && (
              <select
                value={sendLang}
                onChange={(e) => {
                  setSendLang(e.target.value);
                  setSendLangManual(true);
                  lsSet(lsSendLangKey(sessionKey), e.target.value);
                }}
                aria-label="翻訳先の言語"
                style={{ border: 'none', borderLeft: '1px solid #86efac', background: 'transparent', color: '#16a34a', fontSize: '11px', fontWeight: 700, padding: '0 4px 0 6px', height: '32px', cursor: 'pointer', outline: 'none' }}
              >
                <option value="">言語を選択</option>
                {RESV_TRANSLATE_LANGS.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            )}
          </Box>
          {translateTo && (
            <span style={{ fontSize: '11px', color: '#475569' }}>日本語で入力 → {sendLangLabel}でお客様へ（原文はパネルに残ります）</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          {fetchTemplates && (
            <Tooltip title="テンプレート（LINE ページのテンプレート）">
              <span>
                <IconButton onClick={handleOpenTemplates} disabled={isArchived} sx={{ color: '#2563eb' }} aria-label="テンプレート">
                  <FlashOnIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Menu anchorEl={templateAnchor} open={Boolean(templateAnchor)} onClose={() => setTemplateAnchor(null)} sx={{ zIndex: 10600 }}
            PaperProps={{ sx: { width: '320px', maxHeight: '400px', borderRadius: '12px' } }}>
            {templateList.length === 0 ? (
              <MenuItem disabled sx={{ fontSize: '13px' }}>テンプレートが登録されていません</MenuItem>
            ) : (
              [...templateCats, { id: null, name: '未分類' }].map((cat: any) => {
                const items = templateList.filter((t) => (cat.id === null ? !t.category_id : String(t.category_id) === String(cat.id)));
                if (items.length === 0) return null;
                return [
                  <Box key={`cat-${cat.id}`} px={2} py={0.5} sx={{ bgcolor: '#f1f5f9' }}>
                    <Typography variant="caption" fontWeight={800} color="textSecondary">{cat.name}</Typography>
                  </Box>,
                  ...items.map((t) => (
                    <MenuItem key={t.id} onClick={() => handleSelectTemplate(t)} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                      <Typography variant="subtitle2" fontWeight={800} color="primary">{t.name}</Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.content}</Typography>
                    </MenuItem>
                  )),
                ];
              })
            )}
          </Menu>
          <Tooltip title="AI 返信案（LP チャットの AI が最新の顧客メッセージから作成）">
            <span>
              <IconButton onClick={handleSuggest} disabled={suggesting || isArchived} sx={{ color: '#4f46e5' }}>
                {suggesting ? <CircularProgress size={20} /> : <SmartToyIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="画像を送る（JPEG / PNG・10MB まで。免許証の写真はメールで受け取る方針）">
            <span>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePickImage} />
              <IconButton onClick={() => fileInputRef.current?.click()} disabled={uploading || isArchived} sx={{ color: '#0e7490' }} aria-label="画像を送る">
                {uploading ? <CircularProgress size={20} /> : <AddPhotoAlternateIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as any).isComposing) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={isArchived ? '対応完了で終了した会話です（取り消すと返信できます）' : translateTo ? `日本語で入力 → ${sendLangLabel}に翻訳して送信（Enter で送信）` : 'スタッフとして返信を入力...（Enter で送信・Shift+Enter で改行）'}
            className="line-chat-input"
            rows={4}
            disabled={isArchived}
            style={{ flex: 1, padding: '10px 14px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '14px', resize: 'none', maxHeight: '200px', overflowY: 'auto', lineHeight: 1.5, outline: 'none' }}
          />
          <button
            onClick={handleSend}
            disabled={!inputText.trim() || sending || isArchived}
            aria-label={translateTo ? `翻訳して送信（→${sendLangLabel}）` : '送信'}
            title={translateTo ? `翻訳して送信（→${sendLangLabel}）` : '送信'}
            style={{
              background: inputText.trim() && !isArchived ? (translateTo ? '#d97706' : '#0e7490') : '#e2e8f0',
              color: 'white',
              border: 'none',
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              cursor: inputText.trim() && !sending && !isArchived ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: 'white' }} /> : translateTo ? <span style={{ fontSize: '16px', lineHeight: 1 }}>🌐</span> : <SendIcon fontSize="small" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReservationChatPanel;
